<!-- frontend/src/views/Login.vue -->
<template>
  <div class="login-page">
    <!-- 上方 NCHU 校徽橫幅 -->
    <div class="nchu-banner">
      <img src="/images/nchu_logo_gold2.png" alt="國立中興大學" class="nchu-logo" />
    </div>

    <div class="login-container">
      <!-- 系統名稱 -->
      <div class="system-title">
        <h1>國立中興大學自主學習護照系統</h1>
        <p class="subtitle">National Chung Hsing University Self-Directed Learning Passport System</p>
      </div>

      <div class="login-card">
        <!-- 錯誤訊息 -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <!-- 成功訊息 -->
        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>

        <!-- 登入方式切換 -->
        <div class="login-tabs">
          <button
            :class="['tab-btn', { active: loginMode === 'account' }]"
            @click="loginMode = 'account'"
          >
            帳號密碼
          </button>
          <button
            :class="['tab-btn', { active: loginMode === 'sso' }]"
            @click="loginMode = 'sso'"
          >
            SSO 登入
          </button>
        </div>

        <!-- SSO 登入區塊 -->
        <div v-if="loginMode === 'sso'" class="sso-section">
          <button
            @click="loginWithSSO"
            class="sso-btn"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="loading-spinner"></span>
            <span>{{ isLoading ? '正在連接...' : '使用中興大學帳號登入' }}</span>
          </button>

          <p class="login-hint">
            學生請使用學號登入 / 教職員請使用職員編號登入
          </p>
        </div>

        <!-- 帳號密碼登入區塊 -->
        <div v-else class="account-section">
          <form @submit.prevent="loginWithAccount">
            <div class="form-group">
              <label for="account">帳號</label>
              <input
                type="text"
                id="account"
                v-model="account"
                placeholder="請輸入帳號"
                :disabled="isLoading"
                autocomplete="username"
              >
            </div>

            <div class="form-group">
              <label for="password">密碼</label>
              <div class="password-wrapper">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  v-model="password"
                  placeholder="請輸入密碼"
                  :disabled="isLoading"
                  autocomplete="current-password"
                >
                <button
                  type="button"
                  class="toggle-password"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  {{ showPassword ? '隱藏' : '顯示' }}
                </button>
              </div>
            </div>

            <button
              type="submit"
              class="login-btn"
              :disabled="isLoading || !account || !password"
            >
              <span v-if="isLoading" class="loading-spinner"></span>
              <span v-else>登入</span>
            </button>
          </form>

          <p class="login-hint">
            僅供管理者及助教使用帳號密碼登入<br>
            一般使用者請至<a href="https://portal.nchu.edu.tw" target="_blank" rel="noopener">單一簽入入口</a>的系統入口進入
          </p>
        </div>

        <!-- 忘記密碼提示 -->
        <p class="forgot-hint">
          忘記密碼 / 重設密碼請至本校<a href="https://portal.nchu.edu.tw" target="_blank" rel="noopener">單一入口</a>修改
        </p>
      </div>

      <!-- 聯絡資訊 -->
      <div class="contact-section">
        <img src="/images/cge_logo.jpeg" alt="通識教育中心" class="cge-logo" />
        <div class="contact-details">
          <p>聯絡我們</p>
          <p>地址：402 臺中市南區興大路145號 綜合教學大樓Y602室</p>
          <p>電話：(04) 22840597　傳真：(04) 22857861</p>
          <p>E-mail：cah@dragon.nchu.edu.tw</p>
        </div>
      </div>

      <!-- 管理者登入 & 版權 -->
      <div class="footer-section">
        <button
          v-if="loginMode !== 'account'"
          class="admin-login-link"
          @click="loginMode = 'account'"
        >
          管理者登入
        </button>
        <p class="copyright">版權所有 &copy; 國立中興大學通識教育中心</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { getApiBaseUrl } from '@/api.js'

export default {
  name: 'Login',
  data() {
    return {
      loginMode: 'sso',
      account: '',
      password: '',
      showPassword: false,
      errorMessage: '',
      successMessage: '',
      isLoading: false
    }
  },
  mounted() {
    const error = this.$route.query.error
    if (error) {
      this.errorMessage = this.getErrorMessage(error)
    }

    const logout = this.$route.query.logout
    if (logout === 'success') {
      this.successMessage = '您已成功登出'
      setTimeout(() => { this.successMessage = '' }, 3000)
    }
  },
  watch: {
    loginMode() {
      this.errorMessage = ''
      this.successMessage = ''
    }
  },
  methods: {
    getErrorMessage(errorCode) {
      const errorMessages = {
        'sso_failed': 'SSO 認證失敗，請重試',
        'invalid_callback': '無效的回調請求',
        'no_token': '無法取得存取權杖',
        'sso_processing_failed': '處理登入時發生錯誤，請稍後再試',
        'invalid_credentials': '帳號或密碼錯誤',
        'account_disabled': '此帳號已被停用',
        'session_expired': '登入已過期，請重新登入'
      }
      return errorMessages[errorCode] || '登入失敗，請重試'
    },

    async loginWithSSO() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const redirect = this.$route.query.redirect || '/'
        localStorage.setItem('redirect_after_login', redirect)

        const apiBaseUrl = getApiBaseUrl().replace('/api', '')
        const response = await axios.get(`${apiBaseUrl}/api/auth/sso/login`)

        if (response.data.auth_url) {
          window.location.href = response.data.auth_url
        } else {
          throw new Error('無法取得 SSO 登入連結')
        }
      } catch (error) {
        console.error('SSO 登入失敗:', error)

        if (error.response) {
          this.errorMessage = error.response.data?.detail || 'SSO 服務暫時無法使用'
        } else if (error.request) {
          this.errorMessage = '無法連接伺服器，請檢查網路連線'
        } else {
          this.errorMessage = 'SSO 登入服務暫時無法使用，請稍後再試'
        }

        this.isLoading = false
      }
    },

    async loginWithAccount() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const apiBaseUrl = getApiBaseUrl().replace('/api', '')

        const response = await axios.post(`${apiBaseUrl}/api/auth/login`, {
          user_id: this.account,
          password: this.password
        })

        if (response.data.access_token) {
          localStorage.setItem('access_token', response.data.access_token)
          localStorage.setItem('token_type', 'bearer')
          if (response.data.user) {
            localStorage.setItem('user_info', JSON.stringify(response.data.user))
          }

          // 通知 App.vue 立即刷新 header（右上角馬上顯示使用者名稱）
          this.$emit('login-success')

          this.successMessage = '登入成功，正在跳轉...'

          const redirect = localStorage.getItem('redirect_after_login') || '/'
          localStorage.removeItem('redirect_after_login')

          setTimeout(() => {
            this.$router.push(redirect)
          }, 500)
        } else {
          throw new Error('登入失敗')
        }
      } catch (error) {
        console.error('帳號密碼登入失敗:', error)

        if (error.response) {
          const status = error.response.status
          if (status === 401) {
            this.errorMessage = '帳號或密碼錯誤'
          } else if (status === 403) {
            this.errorMessage = '此帳號已被停用'
          } else {
            this.errorMessage = error.response.data?.detail || '登入失敗，請重試'
          }
        } else if (error.request) {
          this.errorMessage = '無法連接伺服器，請檢查網路連線'
        } else {
          this.errorMessage = '登入失敗，請重試'
        }

        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: #f5f5f0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* NCHU 校徽橫幅 */
.nchu-banner {
  width: 100%;
  background: #fff;
  border-bottom: 3px solid #b8960c;
  padding: 18px 0;
  text-align: center;
}

.nchu-logo {
  height: 70px;
  object-fit: contain;
}

/* 主容器 */
.login-container {
  width: 100%;
  max-width: 520px;
  padding: 30px 20px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 系統名稱 */
.system-title {
  text-align: center;
  margin-bottom: 28px;
}

.system-title h1 {
  color: #1a5c2e;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
  letter-spacing: 2px;
}

.subtitle {
  color: #888;
  font-size: 12px;
  margin: 0;
  letter-spacing: 0.5px;
}

/* 登入卡片 */
.login-card {
  width: 100%;
  background: #fff;
  border-radius: 8px;
  padding: 32px 36px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
}

/* 訊息提示 */
.error-message {
  background: #fff5f5;
  color: #c53030;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 18px;
  text-align: center;
  font-size: 14px;
  border: 1px solid #feb2b2;
}

.success-message {
  background: #f0fff4;
  color: #276749;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 18px;
  text-align: center;
  font-size: 14px;
  border: 1px solid #9ae6b4;
}

/* Tab 切換 */
.login-tabs {
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 24px;
}

.tab-btn {
  flex: 1;
  padding: 12px 0;
  border: none;
  background: none;
  font-size: 15px;
  font-weight: 600;
  color: #a0aec0;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
}

.tab-btn:hover {
  color: #1a5c2e;
}

.tab-btn.active {
  color: #1a5c2e;
  border-bottom-color: #1a5c2e;
}

/* SSO 區塊 */
.sso-section {
  text-align: center;
}

.sso-btn {
  width: 100%;
  padding: 16px 24px;
  background: #1a5c2e;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.sso-btn:hover:not(:disabled) {
  background: #155026;
}

.sso-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* 帳號密碼區塊 */
.account-section {
  /* no extra margin needed */
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
}

.form-group input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d2d6dc;
  border-radius: 6px;
  font-size: 15px;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #1a5c2e;
  box-shadow: 0 0 0 2px rgba(26, 92, 46, 0.12);
}

.form-group input::placeholder {
  color: #a0aec0;
}

.form-group input:disabled {
  background: #f7fafc;
  cursor: not-allowed;
}

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 60px;
}

.toggle-password {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #718096;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
}

.toggle-password:hover {
  color: #1a5c2e;
}

.login-btn {
  width: 100%;
  padding: 14px;
  background: #1a5c2e;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 6px;
}

.login-btn:hover:not(:disabled) {
  background: #155026;
}

.login-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* 提示文字 */
.login-hint {
  text-align: center;
  margin-top: 18px;
  font-size: 13px;
  color: #888;
  line-height: 1.6;
}

.forgot-hint {
  text-align: center;
  margin: 20px 0 0;
  font-size: 13px;
  color: #999;
  border-top: 1px solid #eee;
  padding-top: 18px;
}

.forgot-hint a {
  color: #1a5c2e;
  text-decoration: none;
  font-weight: 500;
}

.forgot-hint a:hover {
  text-decoration: underline;
}

/* Loading spinner */
.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 聯絡資訊 */
.contact-section {
  display: flex;
  align-items: center;
  gap: 20px;
  background: #fff;
  border-radius: 8px;
  padding: 20px 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  width: 100%;
  margin-bottom: 20px;
}

.cge-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  flex-shrink: 0;
}

.contact-details p {
  margin: 0 0 4px 0;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}

.contact-details p:first-child {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

/* 頁尾 */
.footer-section {
  text-align: center;
}

.admin-login-link {
  background: none;
  border: none;
  color: #999;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  margin-bottom: 10px;
  display: inline-block;
}

.admin-login-link:hover {
  color: #1a5c2e;
}

.copyright {
  color: #aaa;
  font-size: 12px;
  margin: 0;
}

/* 響應式 */
@media (max-width: 480px) {
  .nchu-logo {
    height: 50px;
  }

  .system-title h1 {
    font-size: 20px;
  }

  .login-card {
    padding: 24px 20px;
  }

  .contact-section {
    flex-direction: column;
    text-align: center;
  }
}
</style>
